import {
  NO_HUBSPOT_EMAIL_KEYWORD,
  HUBSPOT_PURCHASE_FORM_ID,
  HUBSPOT_FORM_ID,
} from '../constants'
import { getHubspotCookie } from './getHubspotCookie'
import { getRequestHeaders } from './getRequestHeaders'

export type PostToHubspotProps = {
  email: string
  phoneNumber?: string
  firstName: string
  gdprOptIn: boolean
  lastName: string
  organizationName: string
  jobTitle: string
  seats?: number
  useCase: string
  whenAreYouLookingToPurchase?: string
}

export const postToHubspot = async ({
  email,
  phoneNumber,
  firstName,
  gdprOptIn,
  lastName,
  organizationName,
  jobTitle,
  seats,
  useCase,
  whenAreYouLookingToPurchase,
}: PostToHubspotProps) => {
  const utmSource = new URLSearchParams(window.location.search).get(
    'utm_source',
  )

  const body = {
    formId: HUBSPOT_PURCHASE_FORM_ID,
    submittedAt: Date.now().toString(),
    fields: [
      { name: 'email', value: email },
      { name: 'phone', value: phoneNumber ?? '' },
      { name: 'firstname', value: firstName },
      { name: 'lastname', value: lastName },
      { name: 'company', value: organizationName },
      { name: 'size_of_group__c', value: seats },
      { name: 'use_cases', value: useCase },
      { name: 'jobtitle', value: jobTitle ?? '' },
      { name: 'job_role', value: 'Other' },
      { name: 'utm_source', value: utmSource ?? '' },
      {
        name: 'when_are_you_looking_to_purchase',
        value: whenAreYouLookingToPurchase ?? '',
      },
    ],
    context: {
      hutk: getHubspotCookie(),
      pageUri: 'www.masterclass.com/for-business/purchase',
      pageName: 'MasterClass - For Business - Purchase',
    },
    legalConsentOptions: {
      consent: {
        consentToProcess: gdprOptIn,
        text: 'Consent to process',
        communications: [
          {
            subscriptionTypeId: 26837169,
            text: 'Keep me up to date on MasterClass at Work offerings.',
            value: gdprOptIn,
          },
        ],
      },
    },
  }

  if (email.includes(NO_HUBSPOT_EMAIL_KEYWORD)) {
    // Do not submit to Hubspot if email address is recognized as a testing email
    return { ok: true }
  }

  return fetch('/api/v2/enterprise_orders/submit_to_hubspot', {
    method: 'POST',
    body: JSON.stringify(body),
    ...(await getRequestHeaders()),
  } as RequestInit)
}

export type PostToHubspotV3Props = {
  firstName: string
  lastName: string
  email: string
  jobTitle: string
  employeeSize: string
  organizationName: string
  useCase: string
  whenAreYouLookingToPurchase: string
  purpose: string
  selfServePurchased: string
  gdprOptIn: boolean
}

export const postToHubspotV3 = async ({
  firstName,
  lastName,
  email,
  jobTitle,
  employeeSize,
  organizationName,
  useCase,
  whenAreYouLookingToPurchase,
  purpose,
  selfServePurchased,
  gdprOptIn,
}: PostToHubspotV3Props) => {
  const utmSource = new URLSearchParams(window.location.search).get(
    'utm_source',
  )

  const body = {
    formId: HUBSPOT_FORM_ID,
    submittedAt: Date.now().toString(),
    fields: [
      { name: 'firstname', value: firstName },
      { name: 'lastname', value: lastName },
      { name: 'email', value: email },
      { name: 'job_role', value: jobTitle },
      { name: 'employee_size', value: employeeSize ?? 0 },
      { name: 'company', value: organizationName },
      { name: 'use_cases', value: useCase },
      {
        name: 'when_are_you_looking_to_purchase',
        value: whenAreYouLookingToPurchase,
      },
      { name: 'purpose', value: purpose ?? '' },
      { name: 'utm_source', value: utmSource ?? '' },
      {
        name: 'self_serve_purchased',
        value: selfServePurchased ? 'Yes' : 'No',
      },
    ],
    context: {
      hutk: getHubspotCookie(),
    },
    legalConsentOptions: {
      consent: {
        consentToProcess: gdprOptIn,
        text: 'Consent to process',
        communications: [
          {
            subscriptionTypeId: 26837169,
            text: 'Keep me up to date on MasterClass at Work offerings.',
            value: gdprOptIn,
          },
        ],
      },
    },
  }

  if (email.includes(NO_HUBSPOT_EMAIL_KEYWORD)) {
    // Do not submit to Hubspot if email address is recognized as a testing email
    return { ok: true }
  }

  return fetch('/api/v2/enterprise_orders/submit_to_hubspot', {
    method: 'POST',
    body: JSON.stringify(body),
    ...(await getRequestHeaders()),
  } as RequestInit)
}
